import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="gradient-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <Link to="/" className="inline-flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-tiki-400 to-tiki-600 flex items-center justify-center">
              <span className="text-white font-bold text-lg">T</span>
            </div>
            <span className="text-white font-bold text-xl tracking-tight">
              Tiki<span className="text-tiki-400">Media</span>
            </span>
          </Link>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-16 pt-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-white">Privacy Policy</h1>
          <p className="text-midnight-300 mt-4">Last updated: January 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        <div className="prose prose-lg prose-midnight max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-midnight-900 mb-4">1. Introduction</h2>
            <p className="text-midnight-600 leading-relaxed">
              Tiki Media ("we," "our," or "us") respects your privacy and is committed to protecting your personal data.
              This privacy policy explains how we collect, use, and safeguard your information when you visit our website
              tiki-m.com or engage with our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-900 mb-4">2. Information We Collect</h2>
            <p className="text-midnight-600 leading-relaxed mb-4">We may collect the following types of information:</p>
            <ul className="list-disc list-inside text-midnight-600 space-y-2">
              <li>Personal identification information (name, email address, phone number)</li>
              <li>Business information (company name, job title)</li>
              <li>Technical data (IP address, browser type, device information)</li>
              <li>Usage data (pages visited, time spent on site, referral sources)</li>
              <li>Communication data (messages sent through contact forms or email)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-midnight-600 leading-relaxed mb-4">We use the information we collect to:</p>
            <ul className="list-disc list-inside text-midnight-600 space-y-2">
              <li>Provide, maintain, and improve our services</li>
              <li>Communicate with you about inquiries, projects, and updates</li>
              <li>Analyze website usage to enhance user experience</li>
              <li>Comply with legal obligations</li>
              <li>Protect against fraudulent or unauthorized activity</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-900 mb-4">4. Cookies and Tracking</h2>
            <p className="text-midnight-600 leading-relaxed">
              We use cookies and similar tracking technologies to monitor activity on our website and store certain
              information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
              However, if you do not accept cookies, some portions of our website may not function properly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-900 mb-4">5. Data Sharing</h2>
            <p className="text-midnight-600 leading-relaxed">
              We do not sell, trade, or rent your personal information to third parties. We may share your data with trusted
              service providers who assist us in operating our website and conducting our business, provided they agree to
              keep your information confidential.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-900 mb-4">6. Data Security</h2>
            <p className="text-midnight-600 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal data against
              unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the
              Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-900 mb-4">7. Your Rights</h2>
            <p className="text-midnight-600 leading-relaxed mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-midnight-600 space-y-2">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict processing of your data</li>
              <li>Data portability where applicable</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-900 mb-4">8. Contact Us</h2>
            <p className="text-midnight-600 leading-relaxed">
              If you have any questions about this Privacy Policy, please contact us at:{' '}
              <a href="mailto:contact@tiki-m.com" className="text-tiki-600 hover:text-tiki-700 underline">
                contact@tiki-m.com
              </a>
            </p>
            <p className="text-midnight-600 leading-relaxed mt-2">
              Baal Haakeda 27 A, Tel Aviv-Jaffa, Israel
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-midnight-950 border-t border-midnight-800/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-tiki-400 to-tiki-600 flex items-center justify-center">
                <span className="text-white font-bold text-xs">T</span>
              </div>
              <span className="text-midnight-400 text-sm">
                &copy; {new Date().getFullYear()} Tiki Media. All rights reserved.
              </span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-sm text-midnight-400 hover:text-tiki-400 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-midnight-400 hover:text-tiki-400 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
